import createReducer from 'utils/createReducer';
import uuid from 'utils/id';
import pickBy from 'lodash/pickBy';

import notificationNameSpace from '../constants/reducerNamespace';

import { SUCCESS, INFO, ERROR } from '../constants/types';

// Action constants
export const CREATE_NOTIFICATION = `${notificationNameSpace}/CREATE_NOTIFICATION`;
export const DELETE_NOTIFICATION = `${notificationNameSpace}/DELETE_NOTIFICATION`;

const INIT_STATE = {};
const DEFAULT_DURATION = 5000;

const timers = {};

// Reducers
export default createReducer(INIT_STATE, {
    [CREATE_NOTIFICATION]: (state, { payload }) => ({
        ...state,
        [payload.notificationId]: {
            id: payload.notificationId,
            message: payload.message,
            variation: payload.variation,
            componentId: payload.componentId,
            onClose: payload.onClose,
        },
    }),
    [DELETE_NOTIFICATION]: (state, { payload }) => ({
        ...pickBy(state, (val, key) => key !== payload.deleteId),
    }),
});

// Selectors
export const getAllNotifications = (state) => Object.values(state[notificationNameSpace] || {});

export const getRepeatedNotifications = (state, { message, variation }) => {
    const allNotifications = getAllNotifications(state);
    return allNotifications.filter(
        (notification) => notification.message === message && notification.variation === variation,
    );
};

// Action creators
export const createNotification = (notificationId, message, variation, componentId, onClose) => ({
    type: CREATE_NOTIFICATION,
    payload: {
        notificationId,
        message,
        variation,
        componentId,
        onClose,
    },
});

const closeNotificationActionCreator = (deleteId) => ({
    type: DELETE_NOTIFICATION,
    payload: {
        deleteId,
    },
});

// Thunks
export const closeNotification = (notificationId) => (dispatch, getState) => {
    const state = getState();
    const notification = state.notifications[notificationId];
    if (notification && timers[notification.id]) {
        clearTimeout(timers[notification.id]);
        timers[notification.id] = null;
    }
    dispatch(closeNotificationActionCreator(notificationId));
};

export const closeAllNotifications = () => (dispatch, getState) => {
    const state = getState();
    const notificationsState = state[notificationNameSpace];
    const notificationIds = Object.keys(notificationsState);

    notificationIds.forEach((notificationId) => {
        const notification = state.notifications[notificationId];
        if (notification && timers[notification.id]) {
            clearTimeout(timers[notification.id]);
            timers[notification.id] = null;
        }
        dispatch(closeNotificationActionCreator(notificationId));
    });
};

export const checkRepeatedAndCloseNotifications = (newNotification) => (dispatch, getState) => {
    const state = getState();
    const repeatedNotifications = getRepeatedNotifications(state, newNotification);

    repeatedNotifications.forEach((notification) => dispatch(closeNotificationActionCreator(notification.id)));
};

export const setNewNotification =
    (message, variation, duration = DEFAULT_DURATION, componentId, onClose = () => {}) =>
    (dispatch) => {
        dispatch(checkRepeatedAndCloseNotifications({ message, variation }));

        const notificationId = uuid();
        dispatch(createNotification(notificationId, message, variation, componentId, onClose));

        if (typeof duration === 'number' && duration !== 0) {
            timers[notificationId] = setTimeout(() => {
                onClose();
                dispatch(closeNotificationActionCreator(notificationId));
            }, duration);
        }
        return notificationId;
    };

export const notifyError = (message, duration = 0, onClose) =>
    setNewNotification(message, ERROR, duration, undefined, onClose);
export const notifySuccess = (message, duration = DEFAULT_DURATION, onClose) =>
    setNewNotification(message, SUCCESS, duration, undefined, onClose);
export const notifyInfo = (message, duration = DEFAULT_DURATION, onClose) =>
    setNewNotification(message, INFO, duration, undefined, onClose);
